import { db } from "@/api/firebase";
import { CityPoint } from "@/models/City";
import {
  MAPMOD_TYPE,
  ManhuntMapPoi,
  PUZZLE_TYPE,
} from "@/models/MapPois/ManHunt/ManhuntMapPoi";
import { QuestMapPoi } from "@/models/MapPois/Quest/QuestMapPoi";
import DateHelper from "@/utils/DateHelper";
import ErrorsGame from "@/utils/ErrorsGame";
import WarningsGame from "@/utils/WarningsGame";
import { forIn, toArray } from "lodash";
import Vue from "vue";
import { Module, VuexModule, Action, Mutation } from "vuex-module-decorators";

@Module({ namespaced: true })
export default class MapPoiStore extends VuexModule {
  mapPoi: ManhuntMapPoi[] = [];
  questMapPoi: QuestMapPoi[] = [];
  mapPoiObject = {};
  poiTypes: string[] = [];
  miniGameStyles: string[] = [];
  fxTypes: string[] = [];
  invTypes: string[] = [];
  isEditing = false;
  mapPoiSlug = "";
  dialogQuestionKey = "";

  @Mutation
  setMapPoi(mapPoi: ManhuntMapPoi[]): void {
    this.mapPoi = mapPoi;
  }

  @Mutation
  setQuestMapPoi(mapPoi: QuestMapPoi[]): void {
    this.questMapPoi = mapPoi;
  }

  @Mutation
  setMapPoiSlug(mapPoiSlug: string): void {
    this.mapPoiSlug = mapPoiSlug;
  }

  @Mutation
  setMapPoiObject(mapPoi: any): void {
    this.mapPoiObject = mapPoi;
  }

  @Mutation
  poiType(value: any[]): void {
    this.poiTypes = value;
  }
  @Mutation
  miniGameStyle(value: any[]): void {
    this.miniGameStyles = value;
  }
  @Mutation
  fxType(value: any[]): void {
    this.fxTypes = value;
  }
  @Mutation
  invType(value: any[]): void {
    this.invTypes = value;
  }
  @Mutation
  currentlyEditing(value: boolean): void {
    this.isEditing = value;
  }

  @Mutation
  dialogQuestionFirebaseKey(key: string): void {
    this.dialogQuestionKey = key;
  }

  @Action({ rawError: true })
  public async reinstatePOI(data: any): Promise<void> {
    try {
      await db
        .ref("map_poi/" + data.parentUid + "/" + data.key)
        .once("value")
        .then((result) => {
          if (result.val() !== undefined && result.val() !== null) {
            const templatePoi = result.val();
            templatePoi.loc = data.mapPoi.loc;
            db.ref("map_poi/" + data.gameUid + "/" + data.key).update(
              templatePoi
            );
          }
        });
    } catch (error) {
      console.log(error);
    }
  }
  @Action({ rawError: true })
  public async reinstateQuest(data: any): Promise<void> {
    try {
      await db
        .ref("quests/" + data.parentUid + "/" + data.key)
        .once("value")
        .then((result) => {
          if (result.val() !== undefined && result.val() !== null) {
            const templatePoi = result.val();
            db.ref("quests/" + data.gameUid + "/" + data.key).update(
              templatePoi
            );
          }
        });
    } catch (error) {
      console.log(error);
    }
  }
  @Action({ rawError: true })
  public async getPoiType(slug: string): Promise<void> {
    try {
      const results = await db
        .ref("gametypes/" + slug + "/poiTypes")
        .once("value");
      this.context.commit("poiType", toArray(results.val()));
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async getMiniGameStyle(data: any): Promise<void> {
    try {
      const results = await db
        .ref("gametypes/" + data.type + "/minigameStyles/" + data.name)
        .once("value");
      this.context.commit("miniGameStyle", toArray(results.val()));
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async getFxData(data: any): Promise<void> {
    try {
      const results = await db
        .ref("gametypes/" + data.gt_uid + "/seasons/" + data.seas_uid + "/fx")
        .once("value");
      this.context.commit("fxType", toArray(results.val()));
    } catch (error) {
      console.log(error);
    }
  }
  @Action({ rawError: true })
  public async getInventoryData(data: any): Promise<void> {
    try {
      const results = await db
        .ref(
          "gametypes/" +
            data.gt_uid +
            "/seasons/" +
            data.seas_uid +
            "/inv_items"
        )
        .once("value");
      this.context.commit("invType", toArray(results.val()));
    } catch (error) {
      console.log(error);
    }
  }

  @Action
  public async getMapPoiForGame(key: string): Promise<boolean> {
    try {
      db.ref("map_poi/" + this.mapPoiSlug).off();
      const dbRef = await db.ref("map_poi/" + key);
      this.context.commit("setMapPoiSlug", key);
      dbRef.on("child_added", (data) => {
        const mapPoi: ManhuntMapPoi = data.val();
        mapPoi.key = data.key || "";
        this.mapPoi.push(mapPoi);
      });
      dbRef.on("child_changed", (data) => {
        const results = this.mapPoi.findIndex(
          (x: ManhuntMapPoi) => x.key === data.key
        );
        const mapPoi: ManhuntMapPoi = data.val();
        mapPoi.key = data.key || "";
        Vue.set(this.mapPoi, results, mapPoi);
      });
      dbRef.on("child_removed", (data) => {
        const results = this.mapPoi.findIndex(
          (x: ManhuntMapPoi) => x.key === data.key
        );
        Vue.delete(this.mapPoi, results);
      });
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  @Action
  public async getQuestMapPoiForGame(key: string): Promise<boolean> {
    try {
      db.ref("map_poi/" + this.mapPoiSlug).off();
      const dbRef = await db.ref("map_poi/" + key);
      this.context.commit("setMapPoiSlug", key);
      dbRef.on("child_added", (data) => {
        const mapPoi: QuestMapPoi = data.val();
        mapPoi.key = data.key || "";
        this.questMapPoi.push(mapPoi);
      });
      dbRef.on("child_changed", (data) => {
        const results = this.questMapPoi.findIndex(
          (x: QuestMapPoi) => x.key === data.key
        );
        const mapPoi: QuestMapPoi = data.val();
        mapPoi.key = data.key || "";
        Vue.set(this.questMapPoi, results, mapPoi);
      });
      dbRef.on("child_removed", (data) => {
        const results = this.questMapPoi.findIndex(
          (x: QuestMapPoi) => x.key === data.key
        );
        Vue.delete(this.questMapPoi, results);
      });

      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  @Action
  public async getMapPoiForGameObject(key: string): Promise<void> {
    try {
      await db.ref("map_poi/" + key).once("value", (results) => {
        this.context.commit("setMapPoiObject", results.val());
      });
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async updateMapPoiType(data: any): Promise<string> {
    try {
      if (data.mapPoi.type === "amenities") {
        data.mapPoi.contentGroups = {
          cg0: {
            d1: {
              title: { en: "" },
              txt: { en: "" },
              imgs: "",
              audio: { en: "" },
            },
          },
        };
      }
      await db
        .ref("map_poi/" + data.game_uid + "/" + data.mapPoi.key)
        .update(data.mapPoi);
      return "success";
    } catch (error) {
      console.log(error);
      return "failed";
    }
  }

  @Action({ rawError: true })
  public async clonePoi(data: any): Promise<void> {
    try {
      if (data.location.length <= 0) {
        data.mapPoi.loc.loc_uid = "";
        data.mapPoi.loc.override_ll.latitude = data.lat;
        data.mapPoi.loc.override_ll.longitude = data.lng;
      } else {
        data.mapPoi.loc.loc_uid = data.location;
        data.mapPoi.loc.override_ll.latitude = 0.0;
        data.mapPoi.loc.override_ll.longitude = 0.0;
      }
      data.mapPoi.title.en = data.admin_name;
      const key = await db.ref("/map_poi/" + data.gameUid).push().key;
      db.ref("map_poi/" + data.gameUid + "/" + key).set(data.mapPoi);
    } catch (error) {
      console.log(error);
    }
  }
  @Action({ rawError: true })
  public async getDialogQuestionKey(data: string): Promise<string> {
    console.log(data);
    const key = (await db.ref(data).push().key) || "";
    return key;
  }

  @Action({ rawError: true })
  public async cloneQuestPoi(data: any): Promise<void> {
    try {
      if (data.location.length <= 0) {
        data.mapPoi.loc.loc_uid = "";
        data.mapPoi.loc.override_ll.latitude = data.lat;
        data.mapPoi.loc.override_ll.longitude = data.lng;
      } else {
        data.mapPoi.loc.loc_uid = data.location;
        data.mapPoi.loc.override_ll.latitude = 0.0;
        data.mapPoi.loc.override_ll.longitude = 0.0;
      }
      data.mapPoi.title.en = data.admin_name;
      const key = await db.ref("/map_poi/" + data.gameUid).push().key;
      data.mapPoi.key = key;
      db.ref("map_poi/" + data.gameUid + "/" + key).set(data.mapPoi);
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async insertNewQuestPoi(data: any): Promise<void> {
    try {
      const questPoi: QuestMapPoi = {
        active: data.active,
        type: data.type,
        loc: {
          geo_rad_m: 10,
          loc_uid: data.location,
          override_ll: {
            latitude: 0.0,
            longitude: 0.0,
          },
        },
        title: {
          en: data.admin_name,
        },
        image: data.image,
        visible: data.visible,
        tutorial: false,
        key: "",
        mapStatus: data.mapStatus,
        contentGroups: {
          cg0: {
            d1: {
              title: { en: "" },
              txt: { en: "" },
              imgs: "",
              audio: { en: "" },
            },
          },
        },
      };
      if (data.type === "amenities") {
        if (questPoi.contentGroups !== undefined) {
          //delete questPoi.contentGroups;
        }
      }
      if (data.location.length <= 0) {
        questPoi.loc.loc_uid = "";
        questPoi.loc.override_ll.latitude = data.lat;
        questPoi.loc.override_ll.longitude = data.lng;
      }
      const key = await db.ref("/map_poi/" + data.gameUid).push().key;
      if (key != null) {
        questPoi.key = key;
      }
      db.ref("map_poi/" + data.gameUid + "/" + key).set(questPoi);
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async insertNewPoi(data: any): Promise<void> {
    try {
      const mapPoi: ManhuntMapPoi = {
        active: true,
        type: "person",
        challenge: {
          active: false,
          content: {
            h_thumb: "",
            img: "",
            txt: { en: "" },
          },
          fail_timeout_s: 90,
          max_tries: 2,
          on_fail: {
            active: true,
            fx: {
              ["clock_adjust"]: 5,
            },
          },
          on_incorrect: {
            active: true,
            fx: {
              ["clock_adjust"]: 1,
            },
          },
          on_success: {
            active: false,
          },
          puzzle: {
            q: {
              txt: { en: "" },
              img: "",
            },
            a: {
              a0: {
                correct: false,
                txt: { en: "" },
              },
              a1: {
                correct: false,
                txt: { en: "" },
              },
              a2: {
                correct: false,
                txt: { en: "" },
              },
            },
            type: PUZZLE_TYPE.MULTI_CHOICE,
          },
        },
        default_content: {
          h_thumb: "",
          img: "",
          persist: false,
          txt: { en: "" },
          video: { en: "" },
        },
        loc: {
          geo_rad_m: 10,
          loc_uid: data.location,
          override_ll: {
            latitude: 0.0,
            longitude: 0.0,
          },
        },
        map_mods: {
          active: false,
          set_self_inactive: false,
          runaway: {
            on_fail: {
              active: false,
            },
            on_success: {
              active: false,
            },
            timeout_s: 300,
          },
          type: MAPMOD_TYPE.TRIGGER,
        },
        title: {
          en: data.admin_name,
        },
        visible: true,
        tutorial: false,
        witness: {
          active: false,
          content: {
            ["alt_0"]: {
              main: {
                h_thumb: "",
                img: "",
                txt: { en: "" },
                video: { en: "" },
              },
            },
          },
          guilt: false,
          suspect: false,
        },
        key: "",
      };
      if (data.location.length <= 0) {
        mapPoi.loc.loc_uid = "";
        mapPoi.loc.override_ll.latitude = data.lat;
        mapPoi.loc.override_ll.longitude = data.lng;
      }
      new WarningsGame(
        data.gameData,
        data.gameUid,
        this.mapPoi
      ).checkWarningsErrors();
      new ErrorsGame(
        data.gameUid,
        this.mapPoi,
        data.cityData
      ).checkGameErrors();
      const key = await db.ref("/map_poi/" + data.gameUid).push().key;
      if (key != null) {
        mapPoi.key = key;
      }
      db.ref("map_poi/" + data.gameUid + "/" + key).set(mapPoi);
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async deletePoi(data: any): Promise<void> {
    try {
      await db.ref("/map_poi/" + data.gameUid + "/" + data.key).remove();
    } catch (error) {
      console.log(error);
    }
  }

  /*@Action({ rawError: true })
  public async updateWitnessAlt(data: any): Promise<void> {
    try {
      await db
        .ref("/map_poi/" + data.gameUid + "/" + data.key + "/witness/")
        .update({ content: data.content });
    } catch (error) {
      console.log(error);
    }
  }*/

  @Action({ rawError: true })
  public async addTargetMapPoi(data: any): Promise<void> {
    try {
      await db
        .ref(
          "/map_poi/" +
            data.gameUid +
            "/" +
            data.targetMapPoi.key +
            "/map_mods/"
        )
        .update({ targetedBy: data.targetMapPoi.map_mods.targetedBy });
      await db
        .ref("/map_poi/" + data.gameUid + "/" + data.targetMapPoi.key)
        .update({ active: false });
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async randomisePoiPoints(data: any): Promise<void> {
    try {
      const cityPoints: CityPoint[] = [];
      forIn(data.cityData.locations, (x: CityPoint) => {
        cityPoints.push(x);
      });
      const locations = await DateHelper.shuffle<CityPoint>(cityPoints);
      let count = 0;
      await data.mapPoi.map(async (x: QuestMapPoi) => {
        if (x.title.en === "START_ZONE") {
          x.loc.override_ll.latitude = data.cityData.centre_point.latitude;
          x.loc.override_ll.longitude = data.cityData.centre_point.longitude;
          x.loc.loc_uid = "";
        } else {
          if (cityPoints.length > count) {
            if (!data.ignoreList.includes(x.key)) {
              x.loc.override_ll.latitude = 0.0;
              x.loc.override_ll.longitude = 0.0;
              x.loc.loc_uid = locations[count].slug;
            }
          } else {
            x.loc.override_ll.latitude = 0.0;
            x.loc.override_ll.longitude = 0.0;
            x.loc.loc_uid = "";
          }
        }
        count++;
      });
      await data.mapPoi.map(async (x: QuestMapPoi) => {
        await db.ref("map_poi/" + data.gameKey + "/" + x.key).update(x);
      });
    } catch (error) {
      console.log(error);
    }
  }
  @Action({ rawError: true })
  public async transferData(data: any): Promise<void> {
    try {
      /*console.log(data);
      data.mapPoi.forEach((x: MapPoi) => {
        if (x.err != undefined) {
          db.ref(
            "/map_poi/" + data.gameUid + "/" + x.key + "/issues/err/"
          ).update(x.err);
          db.ref("/map_poi/" + data.gameUid + "/" + x.key + "/err").remove();
        }
        if (x.warn != undefined) {
          console.log(x);
          db.ref(
            "/map_poi/" + data.gameUid + "/" + x.key + "/issues/warn/"
          ).update(x.warn);
          db.ref("/map_poi/" + data.gameUid + "/" + x.key + "/warn").remove();
        }
      });*/
      //await db.ref("/games/" + data.gameUid + "/err").remove();
      //await db.ref("/games/" + data.gameUid + "/warn").remove();
    } catch (error) {
      console.log(error);
    }
  }
}
